import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import TuneupLogo from './TuneupLogo';

const navItems = [
  { name: 'Services',       id: 'services'       },
  { name: 'Products',       id: 'products'       },
  { name: 'Analysis',       id: 'analysis'       },
  { name: 'Founding Offer', id: 'founding-offer' },
  { name: 'Contact',        id: 'contact'        },
];

const Navigation = ({ isMenuOpen, setIsMenuOpen, onNavClick, activeSection }) => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAboutPage = location.pathname.startsWith('/meet-Joseph');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (id) => {
    if (isMenuOpen) setIsMenuOpen(false);
    if (!isAboutPage) {
      onNavClick(id);
    } else {
      window.location.href = `/#${id}`;
    }
  };

  const Wordmark = () => (
    <span className="flex items-center gap-2.5">
      <TuneupLogo className="w-7 h-7 shrink-0" />
      <span className="text-xl font-black tracking-tight">
        <span className="text-white">TUNEUP</span>
        <span className="text-orange-500">TECH</span>
      </span>
    </span>
  );

  return (
    <>
      <motion.nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-gray-950/95 backdrop-blur-md border-b border-orange-900/30'
            : 'bg-gray-950/80 backdrop-blur-sm'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">

            {/* Wordmark */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {isAboutPage ? (
                <Link to="/" className="cursor-pointer">
                  <Wordmark />
                </Link>
              ) : (
                <button className="cursor-pointer" onClick={() => handleClick('hero')}>
                  <Wordmark />
                </button>
              )}
            </motion.div>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item, index) => {
                const isActive = !isAboutPage && activeSection === item.id;
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => handleClick(item.id)}
                    className="relative text-sm font-medium uppercase tracking-wide transition-colors duration-200 cursor-pointer"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index + 0.3 }}
                  >
                    <span className={isActive ? 'text-orange-500' : 'text-gray-400 hover:text-white'}>
                      {item.name}
                    </span>
                    <motion.span
                      className="absolute -bottom-1 left-0 h-px bg-orange-500"
                      animate={{ width: isActive ? '100%' : '0%' }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    />
                  </motion.button>
                );
              })}

              {/* Meet Joseph link */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <Link
                  to="/meet-Joseph"
                  className={`relative text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
                    isAboutPage ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Meet Joseph
                  {isAboutPage && (
                    <span className="absolute -bottom-1 left-0 h-px bg-orange-500 w-full" />
                  )}
                </Link>
              </motion.div>

              <motion.button
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 text-sm font-black uppercase tracking-wider transition-colors duration-200"
                style={{ borderRadius: '2px' }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleClick('analysis')}
              >
                Get Analysis →
              </motion.button>
            </div>

            {/* Mobile hamburger */}
            <motion.button
              className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 180, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -180, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile full-screen menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <motion.div
              className="absolute inset-0 bg-gray-950/98 backdrop-blur-md"
              onClick={() => setIsMenuOpen(false)}
            />

            <motion.div
              className="absolute inset-y-0 right-0 w-full flex flex-col items-center justify-center gap-2 px-8 bg-gray-950"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {navItems.map((item, index) => {
                const isActive = !isAboutPage && activeSection === item.id;
                return (
                  <motion.button
                    key={item.id}
                    className={`w-full text-left text-3xl font-black uppercase tracking-wide py-4 border-b transition-colors duration-200 cursor-pointer ${
                      isActive
                        ? 'text-orange-500 border-orange-500/40'
                        : 'text-gray-400 border-gray-800 hover:text-white hover:border-gray-600'
                    }`}
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index + 0.1, type: 'spring', stiffness: 260, damping: 22 }}
                    onClick={() => handleClick(item.id)}
                  >
                    <span className="flex items-center justify-between">
                      {item.name}
                      {isActive && (
                        <motion.span className="text-orange-500 text-lg" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                          ●
                        </motion.span>
                      )}
                    </span>
                  </motion.button>
                );
              })}

              {/* Meet Joseph — mobile */}
              <motion.div
                className="w-full"
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, type: 'spring', stiffness: 260, damping: 22 }}
              >
                <Link
                  to="/meet-Joseph"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block w-full text-left text-3xl font-black uppercase tracking-wide py-4 border-b transition-colors duration-200 ${
                    isAboutPage
                      ? 'text-orange-500 border-orange-500/40'
                      : 'text-gray-400 border-gray-800 hover:text-white hover:border-gray-600'
                  }`}
                >
                  Meet Joseph
                </Link>
              </motion.div>

              <motion.button
                className="mt-8 w-full bg-orange-500 hover:bg-orange-600 text-white py-5 font-black text-xl uppercase tracking-wider transition-colors"
                style={{ borderRadius: '2px' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                onClick={() => handleClick('analysis')}
              >
                Get Analysis →
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
